import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Prospective Memory — FTUE demo (3 essais).
 *
 * E1 : tâche en cours seule (digit 4 → tape PAIR).
 * E2 : on rappelle la consigne PM, puis cue (digit 7 → tape PM).
 * E3 : ongoing après PM (digit 5 → tape IMPAIR).
 */
import { useEffect, useRef, useState } from 'react';
const DEMO_STEPS = [
    {
        idx: 1,
        title: 'Essai 1 — tâche en cours',
        hint: 'Indique la parité du chiffre. Ici 4 : tape PAIR.',
        digit: 4,
        expected: 'even',
    },
    {
        idx: 2,
        title: 'Essai 2 — consigne PM',
        hint: "Désormais : si le chiffre est 7, tape PM au lieu de la parité.",
        digit: 7,
        expected: 'pm',
        showPmBriefing: true,
    },
    {
        idx: 3,
        title: 'Essai 3 — retour ongoing',
        hint: '5 n\'est pas la cible PM. Tape IMPAIR.',
        digit: 5,
        expected: 'odd',
    },
];
export function Demo({ onComplete, onSkip }) {
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('fixation');
    const [picked, setPicked] = useState(null);
    const phaseTimer = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    useEffect(() => {
        setPicked(null);
        if (step.showPmBriefing) {
            setPhase('briefing');
            phaseTimer.current = setTimeout(() => setPhase('fixation'), 2000);
        }
        else {
            setPhase('fixation');
            phaseTimer.current = setTimeout(() => setPhase('awaiting'), 600);
        }
        return () => {
            if (phaseTimer.current)
                clearTimeout(phaseTimer.current);
        };
    }, [stepIdx, step.showPmBriefing]);
    useEffect(() => {
        if (phase === 'fixation' && step.showPmBriefing) {
            phaseTimer.current = setTimeout(() => setPhase('awaiting'), 600);
        }
        return () => {
            if (phaseTimer.current)
                clearTimeout(phaseTimer.current);
        };
    }, [phase, step.showPmBriefing]);
    const correct = (() => {
        if (phase !== 'feedback')
            return null;
        return picked === step.expected;
    })();
    const handlePick = (a) => {
        if (phase !== 'awaiting')
            return;
        setPicked(a);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: "D\u00E9mo guid\u00E9e" }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: step.title }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: step.hint }), _jsxs("div", { className: "relative flex h-[200px] w-full max-w-[320px] items-center justify-center", style: { background: '#1a1a1a' }, children: [phase === 'briefing' && (_jsx("span", { className: "font-display text-[20px] text-cyan-surface px-s4 text-center", children: "Si chiffre = 7 \u2192 PM" })), phase === 'fixation' && (_jsx("span", { className: "font-display text-[36px] text-cyan-surface", children: "+" })), (phase === 'awaiting' || phase === 'feedback') && (_jsx("span", { className: "font-display text-[110px] text-cyan-surface", children: step.digit })), phase === 'feedback' && (_jsx("span", { className: [
                            'absolute inset-0 flex items-center justify-center font-display text-[28px] font-medium',
                            correct ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), style: { background: 'rgba(0,0,0,0.7)' }, children: correct ? '✓' : '✗' }))] }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'awaiting' && (_jsxs("div", { className: "flex justify-center gap-s3", children: [_jsx("button", { type: "button", onClick: () => handlePick('odd'), className: "flex h-[52px] min-w-[90px] items-center justify-center border border-cyan-border bg-cyan-surface px-s3 font-display text-[13px] font-medium text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: "Impair" }), _jsx("button", { type: "button", onClick: () => handlePick('even'), className: "flex h-[52px] min-w-[90px] items-center justify-center border border-cyan-border bg-cyan-surface px-s3 font-display text-[13px] font-medium text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: "Pair" }), _jsx("button", { type: "button", onClick: () => handlePick('pm'), className: "flex h-[52px] min-w-[90px] items-center justify-center bg-cyan-core px-s3 font-display text-[13px] font-medium text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: "PM" })] })), phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? 'Démarrer la session' : 'Essai suivant' })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: "Passer la d\u00E9mo" })] })] }));
}
//# sourceMappingURL=demo.js.map